/**
 * ButtonLink — een `<Link>` (react-router) gestyled als Aurora Button.
 *
 * Waarom een aparte component i.p.v. `<Button onClick={() => navigate(...)}>`?
 *
 *  - Semantisch een anchor: rechtsklik → "Open in nieuw tabblad", Ctrl+
 *    click, copy-link, screen-reader herkent navigatie i.p.v. action.
 *  - Aurora-spec §8.1 dekt alleen `<button>`-variants; voor in-app
 *    navigatie binnen rij-acties / inline-CTA's is dit het idiomatische
 *    React Router-equivalent met dezelfde visuele behandeling.
 *
 * Variants/sizes mappen 1:1 op Button — onder water hergebruiken we de
 * Button.module.css zodat states (hover, focus-ring, active scale)
 * identiek zijn aan een echte <Button>.
 */
import { forwardRef } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import buttonStyles from '../Button/Button.module.css';

type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'destructive'
  | 'destructiveGhost';
type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonLinkProps extends Omit<LinkProps, 'className'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
}

const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(
  ({ variant = 'primary', size = 'md', className, children, ...rest }, ref) => {
    const classNames = [
      buttonStyles.button,
      buttonStyles[variant],
      buttonStyles[size],
      className ?? '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <Link ref={ref} className={classNames} {...rest}>
        {children}
      </Link>
    );
  },
);

ButtonLink.displayName = 'ButtonLink';

export { ButtonLink };
